import React from 'react';
import { ResponsiveLine } from '@nivo/line';
import { format } from 'date-fns';

const TimeSeriesChart = ({ 
  data, 
  title, 
  xAxisLabel, 
  yAxisLabel, 
  height = 400,
  showLegend = true,
  legendPosition = 'top-right',
  tickRotation = 0,
  margin = { top: 50, right: 110, bottom: 50, left: 60 },
  dateType = 'monthly', // 'yearly', 'monthly', 'daily'
  xAxisLegendOffset = 70,
  yAxisLegendOffset = -40
}) => {
  // Get all unique x values and create a cleaner tick selection
  const getAllXValues = () => {
    const allXValues = data.flatMap(series => series.data.map(d => d.x));
    // Use Map with timestamp as key for efficient deduplication
    // Map preserves the first occurrence of each unique timestamp
    const uniqueDatesMap = new Map();
    allXValues.forEach(date => {
      const timestamp = date.getTime();
      if (!uniqueDatesMap.has(timestamp)) {
        uniqueDatesMap.set(timestamp, date);
      }
    });
    // Extract values and sort chronologically
    return Array.from(uniqueDatesMap.values()).sort((a, b) => a - b);
  };

  const getCleanTicks = () => {
    const allValues = getAllXValues(); // Already Date objects, already sorted
    if (allValues.length <= 15) return allValues;
    
    // For monthly data - show every 6 months
    if (dateType === 'monthly') {
      const ticks = [];
      const startDate = allValues[0];
      const endDate = allValues[allValues.length - 1];
      
      ticks.push(startDate); // Always include the first date
      
      // Generate 6-month intervals
      let currentDate = new Date(startDate);
      currentDate.setMonth(currentDate.getMonth() + 6);
      
      while (currentDate <= endDate) {
        // Find the closest actual date to this 6-month interval
        let closestDate = allValues[0];
        let minDiff = Math.abs(allValues[0].getTime() - currentDate.getTime());
        
        for (const date of allValues) {
          const diff = Math.abs(date.getTime() - currentDate.getTime());
          if (diff < minDiff) {
            minDiff = diff;
            closestDate = date;
          }
        }
        
        // Only add if it's not already in ticks (compare timestamps)
        if (!ticks.some(t => t.getTime() === closestDate.getTime())) {
          ticks.push(closestDate);
        }
        
        currentDate.setMonth(currentDate.getMonth() + 6); // Move to next 6-month interval
      }
      
      return ticks;
    }
    
    // Default behavior for yearly/daily - evenly spaced ticks
    const startTime = allValues[0].getTime();
    const endTime = allValues[allValues.length - 1].getTime();
    const totalDuration = endTime - startTime;
    const intervalDuration = totalDuration / 14; // 14 intervals = 15 ticks
    
    const ticks = [allValues[0]]; // Always include the first value
    
    // Find the closest data point for each of the 13 intermediate intervals
    for (let i = 1; i < 14; i++) {
      const targetTime = startTime + (i * intervalDuration);
      
      let closestDate = allValues[0];
      let minDiff = Math.abs(allValues[0].getTime() - targetTime);
      
      for (const date of allValues) {
        const diff = Math.abs(date.getTime() - targetTime);
        if (diff < minDiff) {
          minDiff = diff;
          closestDate = date;
        }
      }
      
      // Add if not duplicate (compare timestamps)
      if (!ticks.some(t => t.getTime() === closestDate.getTime())) {
        ticks.push(closestDate);
      }
    }
    
    // Always include the last value if not already included
    const lastValue = allValues[allValues.length - 1];
    if (!ticks.some(t => t.getTime() === lastValue.getTime())) {
      ticks.push(lastValue);
    }
    
    return ticks;
  };
  return (
    <div style={{ height: `${height}px` }} className="bg-white rounded p-4">
      {title && (
        <h3 className="text-lg font-semibold text-gray-800" style={{ marginBottom: '-1rem' }}>{title}</h3>
      )}
      <ResponsiveLine
        data={data}
        margin={margin}
        xScale={{ 
          type: 'time', 
          format: 'native',
          useUTC: true  // Use UTC for consistent timezone handling across all users
        }}
        yScale={{ type: 'linear', min: 0, stacked: false }}
        axisBottom={{ 
          legend: xAxisLabel, 
          legendOffset: xAxisLegendOffset, 
          legendPosition: 'middle',
          tickRotation: tickRotation,
          tickPadding: 10,
          tickValues: getCleanTicks(),
          format: (value) => {
            // Format based on dateType - value is already a Date object
            try {
              switch (dateType) {
                case 'yearly':
                  return format(value, 'yyyy');
                case 'monthly':
                  return format(value, 'MMM yyyy');
                case 'daily':
                  return format(value, 'MMM dd');
                default:
                  return format(value, 'MMM yyyy');
              }
            } catch (e) {
              console.error('Date formatting error:', e);
              return String(value);
            }
          }
        }}
        axisLeft={{ 
          legend: yAxisLabel, 
          legendOffset: yAxisLegendOffset, 
          legendPosition: 'middle' 
        }}
        pointSize={6}
        pointBorderWidth={2}
        useMesh={true}
        colors={{ scheme: 'category10' }}
        legends={showLegend ? [{
          anchor: legendPosition,
          direction: 'column',
          justify: false,
          translateX: legendPosition === 'top-right' ? 100 : 0,
          translateY: 0,
          itemsSpacing: 4,
          itemDirection: 'left-to-right',
          itemWidth: 80,
          itemHeight: 20,
          symbolSize: 12,
          symbolShape: 'circle',
          effects: [{ on: 'hover', style: { itemBackground: 'rgba(0, 0, 0, .03)', itemOpacity: 1 } }]
        }] : []}
        tooltip={({ point }) => {
          // Get all data points for the same x value (year/month)
          const pointTimestamp = point.data.x.getTime();
          const allPointsForX = data
            .flatMap(series => series.data)
            .filter(dataPoint => dataPoint.x.getTime() === pointTimestamp)
            .sort((a, b) => b.y - a.y); // Sort by count descending
          
          // Format the x-value for display
          const displayDate = (() => {
            try {
              const date = point.data.x; // Already a Date object with native format
              switch (dateType) {
                case 'yearly':
                  return format(date, 'yyyy');
                case 'monthly':
                  return format(date, 'MMM yyyy');
                case 'daily':
                  return format(date, 'MMM dd, yyyy');
                default:
                  return format(date, 'MMM yyyy');
              }
            } catch (e) {
              console.error('Error formatting date in tooltip:', e);
              return String(point.data.x);
            }
          })();
          
          return (
            <div className="bg-white p-4 rounded shadow-lg border" style={{ minWidth: 'max-content' }}>
              <div className="font-semibold text-gray-800 mb-3 text-center">
                {displayDate}
              </div>
              <div className="space-y-1">
                {allPointsForX.map((dataPoint, index) => {
                  // Find the series color for this data point
                  const series = data.find(s => s.data.some(d => d.x.getTime() === dataPoint.x.getTime() && d.y === dataPoint.y));
                  const seriesIndex = data.findIndex(s => s.id === series?.id);
                  const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];
                  const color = colors[seriesIndex % colors.length];
                  
                  return (
                    <div key={index} className="flex items-center text-sm">
                      <div 
                        className="w-3 h-3 rounded-sm mr-2 flex-shrink-0" 
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-gray-700 whitespace-nowrap">{series?.id || 'Unknown'}</span>
                      <span className="ml-2 font-medium text-gray-900 whitespace-nowrap">
                        {dataPoint.y.toLocaleString()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};

export default TimeSeriesChart;
